import { useCallback } from "react";

import { MutationFunctionOptions, MutationTuple, OperationVariables, useMutation } from "@apollo/client";
import { toast } from "sonner";

import { Link } from "@chakra-ui/next-js";

import { graphql } from "@/__generated__";
import {
  EnvironmentsCustomWorkflowError,
  EnvironmentsPatchError,
  EnvironmentsSetupError,
  EnvironmentsTeardownError,
  MEnvironmentPipelineMutation_EnvironmentCustomWorkflowMutation,
  MEnvironmentPipelineMutation_EnvironmentCustomWorkflowMutationVariables,
  MEnvironmentPipelineMutation_EnvironmentPatchMutation,
  MEnvironmentPipelineMutation_EnvironmentPatchMutationVariables,
  MEnvironmentPipelineMutation_EnvironmentSetupMutation,
  MEnvironmentPipelineMutation_EnvironmentSetupMutationVariables,
  MEnvironmentPipelineMutation_EnvironmentTeardownMutation,
  MEnvironmentPipelineMutation_EnvironmentTeardownMutationVariables,
} from "@/__generated__/graphql";
import { assertHasNoUserErrors } from "@/utils/legacyErrorHandler";
import { operationErrorHandler } from "@/utils/operationErrorHandler";
import { buildCreatePath, deployPath } from "@/utils/pathGenerators";
import { isUserError } from "@/utils/typeGuards";

const MEnvironmentPipelineMutation_EnvironmentSetup = graphql(`
  mutation MEnvironmentPipelineMutation_EnvironmentSetup($input: EnvironmentsSetupInput!) {
    pipelineMutation: environmentsSetup(input: $input) {
      pipeline {
        id
        databaseId
      }
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

const MEnvironmentPipelineMutation_EnvironmentPatch = graphql(`
  mutation MEnvironmentPipelineMutation_EnvironmentPatch($input: EnvironmentsPatchInput!) {
    pipelineMutation: environmentsPatch(input: $input) {
      pipeline {
        id
        databaseId
      }
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

const MEnvironmentPipelineMutation_EnvironmentTeardown = graphql(`
  mutation MEnvironmentPipelineMutation_EnvironmentTeardown($input: EnvironmentsTeardownInput!) {
    pipelineMutation: environmentsTeardown(input: $input) {
      pipeline {
        id
        databaseId
      }
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

const MEnvironmentPipelineMutation_EnvironmentCustomWorkflow = graphql(`
  mutation MEnvironmentPipelineMutation_EnvironmentCustomWorkflow($input: EnvironmentsCustomWorkflowInput!) {
    pipelineMutation: environmentsCustomWorkflow(input: $input) {
      pipeline {
        id
        databaseId
      }
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

const MEnvironmentPipelineMutation_Resume = graphql(`
  mutation MEnvironmentPipelineMutation_Resume($input: EnvironmentsResumeInput!) {
    environmentsResume(input: $input) {
      environment {
        ...EnvironmentActions_Environment
      }
      userErrors {
        ... on OperationError {
          message
        }
      }
    }
  }
`);

type RequiredIDs = {
  workspaceId: string;
  applicationId: string;
  environmentId: string;
};

type EnvironmentPipelineFunctionBuilderProps<TData, TVariables> = {
  mutation: MutationTuple<TData, TVariables>;
  mutationName: "Setup" | "Patch" | "Teardown" | "Custom Workflow";
  toastId?: string;
  showToast?: boolean;
} & RequiredIDs;

type handlePipelineErrorProps = {
  error: unknown;
  workspaceId: string;
  applicationId: string;
  mutationName: "Setup" | "Patch" | "Teardown" | "Custom Workflow";
  toastId?: string;
  resumeFn: () => Promise<void>;
  setupFn: () => Promise<void>;
};

function handlePipelineError({
  error,
  mutationName,
  workspaceId,
  applicationId,
  toastId,
  resumeFn,
  setupFn,
}: handlePipelineErrorProps) {
  const toastProps = toastId ? { id: toastId } : {};

  if (isUserError(error)) {
    const errors: Array<
      | EnvironmentsSetupError
      | EnvironmentsPatchError
      | EnvironmentsTeardownError
      | EnvironmentsCustomWorkflowError
    > = error;

    let errorTitle: string | undefined;
    switch (mutationName) {
      case "Setup":
      case "Patch":
        errorTitle = `An error occurred while creating the ${mutationName} deploy`;
        break;
      case "Teardown":
        errorTitle = "An errored occurred while creating the Environment Teardown";
        break;
      case "Custom Workflow":
        errorTitle = "An errored occurred while creating the Custom Workflow";
    }
    errors.forEach(mutationError => {
      switch (mutationError.__typename) {
        case "PipelinesNoBuildFoundError": {
          toast.error(errorTitle, {
            description: mutationError.message,
            action: (
              <Link href={buildCreatePath({ workspaceId, applicationId })}>Click here to create a Build</Link>
            ),
            ...toastProps,
          });
          return;
        }
        case "PipelinesDeployCreationError": {
          toast.error(errorTitle, {
            description: mutationError.message,
          });
          return;
        }
        case "PipelinesEnvironmentNotRunningError": {
          toast.error(errorTitle, {
            description: mutationError.message,
            action: {
              label: "Resume Environment",
              onClick: resumeFn,
            },
            ...toastProps,
          });
          return;
        }
        case "PipelinesEnvironmentNotSetupError": {
          toast.error(errorTitle, {
            description: mutationError.message,
            action: {
              label: "Setup Environment",
              onClick: setupFn,
            },
            ...toastProps,
          });
          return;
        }
        default: {
          toast.error("An error occurred", {
            description: mutationError.message,
            ...toastProps,
          });
        }
      }
    });
  } else {
    const runtimeError: Error = error as Error;
    toast.error("An unexpected error occurred", {
      description: runtimeError.message,
      ...toastProps,
    });
  }
}

function useEnvironmentPipelineMutation<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TData extends Record<string, any>,
  TVariables = OperationVariables
>({
  workspaceId,
  applicationId,
  environmentId,
  mutation,
  mutationName,
  toastId,
  showToast = true,
}: EnvironmentPipelineFunctionBuilderProps<TData, TVariables>) {
  const [mutationFn, mutationResult] = mutation;
  const [resume] = useMutation(MEnvironmentPipelineMutation_Resume);
  const onResume = useCallback(async () => {
    try {
      await resume({
        variables: { input: { id: environmentId } },
      });
    } catch (error) {
      operationErrorHandler(error);
    }
  }, [resume, environmentId]);
  const [setup] = useMutation(MEnvironmentPipelineMutation_EnvironmentSetup);
  const onSetup = useCallback(async () => {
    try {
      const { data } = await setup({
        variables: { input: { id: environmentId } },
      });
      const deployId = data?.pipelineMutation?.pipeline?.databaseId;
      if (!deployId) throw new Error("Missing databaseId");

      toast.success("Setup Created", {
        duration: 5000,
        action: (
          <Link
            href={deployPath({
              workspaceId,
              applicationId,
              environmentId,
              deployId,
            })}
            sx={{ textDecoration: "underline", marginLeft: "auto" }}
          >
            View Setup
          </Link>
        ),
      });
    } catch (error) {
      operationErrorHandler(error);
    }
  }, [workspaceId, applicationId, environmentId, setup]);

  const wrappedMutationFn = useCallback(
    async (mutationVariables?: MutationFunctionOptions<TData, TVariables>) => {
      try {
        const { data } = mutationVariables ? await mutationFn(mutationVariables) : await mutationFn();
        assertHasNoUserErrors(data?.pipelineMutation?.userErrors);

        const deployId = data.pipelineMutation?.pipeline?.databaseId;
        if (!deployId) throw new Error("Missing databaseId");

        if (showToast) {
          toast.success(`${mutationName} Created`, {
            id: toastId,
            duration: 5000,
            action: (
              <Link
                href={deployPath({
                  workspaceId,
                  applicationId,
                  environmentId,
                  deployId,
                })}
                sx={{ textDecoration: "underline", marginLeft: "auto" }}
              >
                {`View ${mutationName}`}
              </Link>
            ),
          });
        }
      } catch (error) {
        handlePipelineError({
          error,
          mutationName,
          workspaceId,
          applicationId,
          resumeFn: onResume,
          setupFn: onSetup,
        });
      }
    },
    [
      workspaceId,
      applicationId,
      environmentId,
      mutationFn,
      mutationName,
      toastId,
      showToast,
      onResume,
      onSetup,
    ]
  );

  return [wrappedMutationFn, mutationResult] as const;
}

function useEnvironmentSetupMutation({
  workspaceId,
  applicationId,
  environmentId,
  checkinPools = false,
  toastId,
  showToast,
}: RequiredIDs & { checkinPools?: boolean; toastId?: string; showToast?: boolean }) {
  const mutation = useMutation(MEnvironmentPipelineMutation_EnvironmentSetup, {
    variables: { input: { id: environmentId, checkinPools } },
  });

  return useEnvironmentPipelineMutation<
    MEnvironmentPipelineMutation_EnvironmentSetupMutation,
    MEnvironmentPipelineMutation_EnvironmentSetupMutationVariables
  >({
    workspaceId,
    applicationId,
    environmentId,
    mutation,
    mutationName: "Setup",
    toastId,
    showToast,
  });
}

function useEnvironmentPatchMutation({
  workspaceId,
  applicationId,
  buildId,
  environmentId,
  toastId,
  showToast,
}: RequiredIDs & { toastId?: string; showToast?: boolean; buildId?: string }) {
  const mutation = useMutation(MEnvironmentPipelineMutation_EnvironmentPatch, {
    variables: { input: { id: environmentId, buildId } },
  });

  return useEnvironmentPipelineMutation<
    MEnvironmentPipelineMutation_EnvironmentPatchMutation,
    MEnvironmentPipelineMutation_EnvironmentPatchMutationVariables
  >({
    workspaceId,
    applicationId,
    environmentId,
    mutation,
    mutationName: "Patch",
    toastId,
    showToast,
  });
}

function useEnvironmentTeardownMutation({
  workspaceId,
  applicationId,
  environmentId,
  continueOnError,
  toastId,
  showToast,
}: RequiredIDs & { continueOnError?: boolean; toastId?: string; showToast?: boolean }) {
  const mutation = useMutation(MEnvironmentPipelineMutation_EnvironmentTeardown, {
    variables: { input: { id: environmentId, continueOnError } },
  });

  return useEnvironmentPipelineMutation<
    MEnvironmentPipelineMutation_EnvironmentTeardownMutation,
    MEnvironmentPipelineMutation_EnvironmentTeardownMutationVariables
  >({
    workspaceId,
    applicationId,
    environmentId,
    mutation,
    mutationName: "Teardown",
    toastId,
    showToast,
  });
}

function useEnvironmentCustomWorkflowMutation({
  workspaceId,
  applicationId,
  environmentId,
  toastId,
  showToast,
}: RequiredIDs & { toastId?: string; showToast?: boolean }) {
  const mutation = useMutation(MEnvironmentPipelineMutation_EnvironmentCustomWorkflow);
  const [pipelineMutation] = useEnvironmentPipelineMutation<
    MEnvironmentPipelineMutation_EnvironmentCustomWorkflowMutation,
    MEnvironmentPipelineMutation_EnvironmentCustomWorkflowMutationVariables
  >({
    workspaceId,
    applicationId,
    environmentId,
    mutation,
    mutationName: "Custom Workflow",
    toastId,
    showToast,
  });
  return (workflowName: string) => {
    const mutationVariables = {
      variables: { input: { id: environmentId, workflowName } },
    };
    return pipelineMutation(mutationVariables);
  };
}

export {
  useEnvironmentCustomWorkflowMutation,
  useEnvironmentPatchMutation,
  useEnvironmentPipelineMutation,
  useEnvironmentSetupMutation,
  useEnvironmentTeardownMutation,
};
